import {
  useCallback,
  useRef,
  MutableRefObject,
  MouseEvent,
  TouchEvent,
} from "react";
import { ClientPosition } from "../types/zoom";

const DEFAULT_DELAY = 300;

type TapMode = "single" | "double";

interface UseTapParams {
  onTap: (event: ClientPosition) => void;
  hasDraggedRef: MutableRefObject<boolean>;
  isPinchingRef: MutableRefObject<boolean>;
  touchTapMode?: TapMode;
  mouseTapMode?: TapMode;
  delay?: number;
}

interface UseTapReturn {
  onClick: (event: MouseEvent) => void;
  onTouchEnd: (event: TouchEvent) => void;
  onDoubleClick: (event: MouseEvent) => void;
}

export default function useTap({
  onTap,
  hasDraggedRef,
  isPinchingRef,
  touchTapMode = "double",
  mouseTapMode = "double",
  delay = DEFAULT_DELAY,
}: UseTapParams): UseTapReturn {
  const lastClickTimeRef = useRef<number>(0);
  const lastClickEventRef = useRef<ClientPosition | null>(null);
  const lastTapTimeRef = useRef<number>(0);
  const lastTapEventRef = useRef<ClientPosition | null>(null);
  const recentTouchRef = useRef<boolean>(false);

  const onClick = useCallback(
    (event: MouseEvent): void => {
      if (recentTouchRef.current) {
        recentTouchRef.current = false;
        return;
      }

      if (hasDraggedRef.current) {
        hasDraggedRef.current = false;
        return;
      }

      if (isPinchingRef.current) {
        return;
      }

      if (mouseTapMode === "single") {
        onTap(event);
        return;
      }

      const now = Date.now();
      if (now - lastClickTimeRef.current < delay) {
        onTap(lastClickEventRef.current);
        lastTapTimeRef.current = 0;
        lastTapEventRef.current = null;
        lastClickTimeRef.current = 0;
        lastClickEventRef.current = null;
      } else {
        lastClickTimeRef.current = now;
        lastClickEventRef.current = event;
      }
    },
    [onTap, hasDraggedRef, isPinchingRef, mouseTapMode, delay],
  );

  const onTouchEnd = useCallback(
    (event: TouchEvent): void => {
      if (isPinchingRef.current) {
        if (event.touches.length === 0) {
          isPinchingRef.current = false;
        }
        return;
      }

      if (hasDraggedRef.current) {
        return;
      }

      recentTouchRef.current = true;

      if (touchTapMode === "single") {
        event.preventDefault();
        const touch = event.changedTouches?.[0];
        onTap({
          clientX: touch?.clientX ?? 0,
          clientY: touch?.clientY ?? 0,
        });
        return;
      }

      const now = Date.now();
      if (now - lastTapTimeRef.current < delay) {
        event.preventDefault();
        onTap(lastTapEventRef.current);
        lastTapTimeRef.current = 0;
        lastTapEventRef.current = null;
        lastClickTimeRef.current = 0;
        lastClickEventRef.current = null;
      } else {
        lastTapTimeRef.current = now;
        const touch = event.changedTouches?.[0];
        lastTapEventRef.current = {
          clientX: touch?.clientX ?? 0,
          clientY: touch?.clientY ?? 0,
        };
      }
    },
    [onTap, hasDraggedRef, isPinchingRef, touchTapMode, delay],
  );

  const onDoubleClick = useCallback((event: MouseEvent): void => {
    event.preventDefault();
  }, []);

  return { onClick, onTouchEnd, onDoubleClick };
}
